/**
 * USE AUTH HOOK
 * Custom hook for authentication operations
 */

import { useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/store';
import { loginSuccess, logout, setLoading, setError, updateUser } from '@/store/slices/authSlice';
import { authApi, sessionUserFrom } from '@/services/api/authApi';
import { LoginCredentials } from '@/types/auth.types';
import { toast } from 'react-toastify';
import { SKIP_AUTH } from '@/config/skipAuth';

let bootstrapPromise: Promise<void> | null = null;

export function useAuth() {
  const dispatch = useAppDispatch();
  const { isAuthenticated, user, loading, error, accessToken } = useAppSelector((state) => state.auth);

  useEffect(() => {
    if (SKIP_AUTH || user || !accessToken) {
      return;
    }

    if (!bootstrapPromise) {
      dispatch(setLoading(true));
      bootstrapPromise = (async () => {
        try {
          // /me answers { user } (the auth service's profile), not an { success, data } envelope.
          const response = await authApi.getCurrentUser();
          if (response?.user?._id) {
            dispatch(updateUser(sessionUserFrom(response.user)));
          }
        } catch (err) {
          // Keep tokens; user will be forced to re-login if session is invalid
          console.warn('Auth bootstrap failed', err);
        } finally {
          dispatch(setLoading(false));
          bootstrapPromise = null;
        }
      })();
    }
  }, [accessToken, user, dispatch]);

  // Global session-expiry handler. Any part of the app (e.g. the chart shape loader)
  // that gets a 401 dispatches `aurexis:session-expired`; here we clear the stale
  // session so ProtectedRoute redirects to /login. This is what turns "my drawings
  // silently vanished" into a clear, recoverable "please sign in again".
  useEffect(() => {
    if (SKIP_AUTH) return;
    const onExpired = () => {
      console.warn('[auth] session expired — clearing tokens and redirecting to login');
      dispatch(logout());
    };
    window.addEventListener('aurexis:session-expired', onExpired);
    return () => window.removeEventListener('aurexis:session-expired', onExpired);
  }, [dispatch]);

  /**
   * Login user
   */
  const login = useCallback(
    async (credentials: LoginCredentials) => {
      dispatch(setLoading(true));
      dispatch(setError(null));

      try {
        const response = await authApi.login(credentials);

        if (response && response.token && response.user) {
          dispatch(loginSuccess({
            user: sessionUserFrom(response.user),
            accessToken: response.token,
            refreshToken: response.refreshToken,
          }));
          toast.success('Login successful!');
          return { success: true };
        }

        const errorMessage = 'Login failed';
        dispatch(setError(errorMessage));
        toast.error(errorMessage);
        return { success: false, error: errorMessage };
      } catch (error: any) {
        const errorMessage = error.message || 'Login failed';
        dispatch(setError(errorMessage));
        toast.error(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        dispatch(setLoading(false));
      }
    },
    [dispatch]
  );

  /**
   * Logout user
   */
  const logoutUser = useCallback(async () => {
    try {
      await authApi.logout();
      dispatch(logout());
      toast.success('Logged out successfully');
    } catch (error: any) {
      // Still logout on client side even if API call fails
      dispatch(logout());
      console.error('Logout error:', error);
    }
  }, [dispatch]);

  /**
   * Get current user
   */
  const getCurrentUser = useCallback(async () => {
    try {
      const response = await authApi.getCurrentUser();
      return response?.user?._id ? sessionUserFrom(response.user) : null;
    } catch (error) {
      console.error('Get current user error:', error);
      return null;
    }
  }, []);

  /**
   * Check if user has permission
   */
  const hasPermission = useCallback(
    (permission: string): boolean => {
      if (!user) return false;
      // The auth service sends no permissions array; a stored session from before may lack it too.
      return Array.isArray(user.permissions) && user.permissions.includes(permission);
    },
    [user]
  );

  /**
   * Check if user has role
   */
  const hasRole = useCallback(
    (role: string): boolean => {
      if (!user) return false;
      return user.role === role;
    },
    [user]
  );

  return {
    isAuthenticated,
    user,
    loading,
    error,
    accessToken,
    login,
    logout: logoutUser,
    getCurrentUser,
    hasPermission,
    hasRole,
  };
}
